import { useQuery } from '@apollo/client';
import {
  ActionIcon,
  Affix,
  ColorSwatch,
  Container,
  Divider,
  Group,
  Loader,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import {
  IconBolt,
  IconCircleCheck,
  IconFlame,
  IconHourglassLow,
  IconRefresh,
  IconTrendingDown,
  IconZzz,
} from '@tabler/icons-react';
import type { IconProps } from '@tabler/icons-react';
import { useRef, useState, type ForwardRefExoticComponent, type RefAttributes } from 'react';
import { useParams } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { ProductsTabletQuery, SortOrder } from './__generated__/graphql';
import { ordersTabletSummaryDocument } from './features/order/graphql/queries';
import {
  productsTabletDocument,
  productTabletSummaryDocument,
} from './features/product/graphql/get-products';
import { colorMap } from './utils/colorMap';
import { liveMottoDocument } from './features/live/graphql/queries';

type SpeedBucket = 'HOT' | 'ACTIVE' | 'COOLING' | 'SLOW' | 'DONE' | 'SOLD_OUT';

// Buckets are computed from 30-second sales windows on the server side
// (recent = last 30s, prev = 30-60s ago). Calibrated against real live-sale
// data — see comment.resolver / product.resolver for window definitions.
function deriveSpeedBucket(
  recent: number,
  prev: number,
  totalStock: number
): SpeedBucket {
  if (totalStock === 0) return 'SOLD_OUT';

  // Absolute HOT — 4+ sales in 30s (≈8/min equivalent)
  if (recent >= 4) return 'HOT';

  // Stock-relative HOT — small batch clearing in ≤5 minutes at current pace.
  // recent×2 ≈ sales/min; stock / (recent×2) ≤ 5 → stock/recent ≤ 10.
  if (recent >= 2 && totalStock / recent <= 10) return 'HOT';

  // COOLING — clear drop from real momentum (halved+ from prev ≥ 3). Checked
  // BEFORE Active so a fade like 6→2 or 5→2 fires Cooling instead of Active.
  if (recent >= 1 && recent < prev / 2 && prev >= 3) return 'COOLING';

  // ACTIVE — steady selling (absolute threshold, no ratio comparison —
  // ratios are too noisy at 30s scale where counts are 2-3).
  if (recent >= 2) return 'ACTIVE';

  // SLOW — trickle pace OR anti-flicker (recently had activity, briefly quiet)
  if (recent >= 1 || prev >= 1) return 'SLOW';

  return 'DONE';
}

type IconComponent = ForwardRefExoticComponent<IconProps & RefAttributes<SVGSVGElement>>;

const BUCKET_META: Record<SpeedBucket, { label: string; icon: IconComponent; colorVar: string }> = {
  HOT: { label: 'HOT', icon: IconFlame, colorVar: 'var(--mantine-color-red-6)' },
  ACTIVE: { label: 'Active', icon: IconBolt, colorVar: 'var(--mantine-color-green-6)' },
  COOLING: {
    label: 'Cooling',
    icon: IconTrendingDown,
    colorVar: 'var(--mantine-color-orange-6)',
  },
  SLOW: { label: 'Slow', icon: IconHourglassLow, colorVar: 'var(--mantine-color-yellow-6)' },
  DONE: { label: 'Done', icon: IconZzz, colorVar: 'var(--mantine-color-gray-5)' },
  SOLD_OUT: { label: 'Sold out', icon: IconCircleCheck, colorVar: 'var(--mantine-color-red-7)' },
};

function formatAge(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h+`;
}

function SpeedBadge({
  recent,
  prev,
  totalStock,
  secondsSinceLastSale,
}: {
  recent: number;
  prev: number;
  totalStock: number;
  secondsSinceLastSale: number | null | undefined;
}) {
  const bucket = deriveSpeedBucket(recent, prev, totalStock);
  const meta = BUCKET_META[bucket];
  const IconComp = meta.icon;
  const showCount = bucket === 'HOT' || bucket === 'ACTIVE' || bucket === 'COOLING';
  const showTime = bucket !== 'SOLD_OUT';

  return (
    <Stack gap={2} align="center">
      <Group gap="xs" wrap="nowrap" align="center">
        <IconComp size={40} color={meta.colorVar} stroke={2} />
        {showCount && (
          <Text fz={36} fw={700} style={{ whiteSpace: 'nowrap' }}>
            {recent}ks
          </Text>
        )}
      </Group>
      {showTime && secondsSinceLastSale != null && (
        <Text fz={24} fw={700} style={{ whiteSpace: 'nowrap' }}>
          {formatAge(secondsSinceLastSale)}
        </Text>
      )}
    </Stack>
  );
}

function renderColorSwatch(attributes?: { name: string; value: string }[]) {
  const colorValue = attributes?.find((a) => a.name === 'farba')?.value;

  if (!colorValue) return null;

  const hex = colorMap[colorValue];

  if (!hex) return null;

  return <ColorSwatch size={16} color={hex} />;
}

const TabletVariant = ({
  product,
  i,
}: {
  product: ProductsTabletQuery['products'][number];
  i: number;
}) => {
  const prevStocksRef = useRef<Map<string, number>>(new Map());
  const prevSecondsRef = useRef<number | null>(null);
  const prevTotalStockRef = useRef<number | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [lastSoldOutId, setLastSoldOutId] = useState<string | null>(null);

  const { data: summaryData } = useQuery(productTabletSummaryDocument, {
    variables: {
      where: { id: product.id },
      variantsOrderBy: { stock: SortOrder.Desc },
    },
    pollInterval: i < 3 ? 1500 : i < 6 ? 3000 : 5000,
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const summary = data.productTabletSummary;
      if (!summary) return;
      const nextVariants = summary.variants ?? [];
      const nextTotalStock = nextVariants.reduce((acc, v) => acc + v.stock, 0);
      const nextSeconds = summary.secondsSinceLastSale ?? null;

      let newSoldOut: string | null = null;
      for (const v of nextVariants) {
        const prevStock = prevStocksRef.current.get(v.id);
        if (prevStock !== undefined && prevStock > 0 && v.stock === 0) {
          newSoldOut = v.id;
        }
        prevStocksRef.current.set(v.id, v.stock);
      }
      if (newSoldOut) setLastSoldOutId(newSoldOut);

      const prevSeconds = prevSecondsRef.current;
      prevSecondsRef.current = nextSeconds;
      if (nextSeconds != null && prevSeconds != null && nextSeconds < prevSeconds) {
        wrapRef.current?.animate(
          [
            {
              backgroundColor: 'rgba(255,255,255,0)',
              boxShadow: '0 0 0 0 rgba(255,255,255,0)',
              transform: 'scale(1)',
            },
            {
              backgroundColor: 'rgba(255,255,255,0.22)',
              boxShadow: '0 0 50px 10px rgba(255,255,255,0.35)',
              transform: 'scale(1.012)',
              offset: 0.2,
            },
            {
              backgroundColor: 'rgba(255,255,255,0)',
              boxShadow: '0 0 0 0 rgba(255,255,255,0)',
              transform: 'scale(1)',
            },
          ],
          { duration: 850, easing: 'ease-out' }
        );
      }

      const prevTotalStock = prevTotalStockRef.current;
      prevTotalStockRef.current = nextTotalStock;
      if (prevTotalStock != null && prevTotalStock > 0 && nextTotalStock === 0) {
        const el = wrapRef.current;
        if (el) {
          const rect = el.getBoundingClientRect();
          const origin = {
            x: (rect.left + rect.width / 2) / window.innerWidth,
            y: (rect.top + rect.height / 2) / window.innerHeight,
          };
          const end = Date.now() + 5000;
          const tick = () => {
            const left = end - Date.now();
            if (left <= 0) return;
            const intensity = left / 5000;
            confetti({
              particleCount: Math.floor(70 * intensity) + 25,
              spread: 90,
              startVelocity: 50,
              scalar: 1.3,
              ticks: 400,
              origin,
            });
            confetti({
              particleCount: Math.floor(35 * intensity) + 15,
              angle: 60,
              spread: 55,
              startVelocity: 55,
              ticks: 400,
              origin,
            });
            confetti({
              particleCount: Math.floor(35 * intensity) + 15,
              angle: 120,
              spread: 55,
              startVelocity: 55,
              ticks: 400,
              origin,
            });
            setTimeout(tick, 300);
          };
          tick();
        }
      }
    },
  });

  const variants = summaryData?.productTabletSummary?.variants;
  const productReservationsCount = summaryData?.productTabletSummary?.reservationsCount ?? 0;
  const recentLinesCount = summaryData?.productTabletSummary?.recentLinesCount ?? 0;
  const prevLinesCount = summaryData?.productTabletSummary?.prevLinesCount ?? 0;
  const secondsSinceLastSale = summaryData?.productTabletSummary?.secondsSinceLastSale ?? null;
  const totalStock = variants?.reduce((acc, v) => acc + v.stock, 0) ?? 0;

  return (
    <Stack ref={wrapRef} gap="xs">
      <Divider />
      <Group justify="space-between">
        <Text fz={48} fw={700}>
          Kód {product.code} - {product.price} €
        </Text>
        <Text fz={48} fw={700}>
          {product.name}
        </Text>
      </Group>
      {product.description && (
        <>
          <Divider />
          <Text fz={36} fw={700}>
            {product.description}
          </Text>
        </>
      )}
      <Table withTableBorder withColumnBorders withRowBorders fz={36} fw={700}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>
              {summaryData ? (
                <SpeedBadge
                  recent={recentLinesCount}
                  prev={prevLinesCount}
                  totalStock={totalStock}
                  secondsSinceLastSale={secondsSinceLastSale}
                />
              ) : (
                'Variant'
              )}
            </Table.Th>
            <Table.Th>
              Sklad ({variants?.reduce((acc, variant) => acc + variant.stock, 0)} ks)
            </Table.Th>
            <Table.Th>
              Predané ({variants?.reduce((acc, variant) => acc + variant.linesCount, 0)} ks)
            </Table.Th>
            <Table.Th>
              Obrat ({variants?.reduce((acc, variant) => acc + variant.linesSum, 0)} €)
            </Table.Th>
            <Table.Th>
              Rezer. (
              {(variants?.reduce((acc, variant) => acc + variant.reservationsCount, 0) ?? 0) +
                productReservationsCount}
              )
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {variants?.map((v) => {
            const justSoldOut = v.stock === 0 && v.id === lastSoldOutId;
            return (
              <Table.Tr
                key={v.id}
                style={
                  justSoldOut ? { backgroundColor: 'var(--mantine-color-red-light)' } : undefined
                }
              >
                <Table.Td
                  c={v.stock === 0 ? 'red' : v.stock < 5 ? 'orange' : undefined}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <Group gap="xs" wrap="nowrap">
                    {renderColorSwatch(v.attributes ?? [])}
                    <Text inherit>{v.attributes?.map((attr) => attr.value).join(' ')}</Text>
                  </Group>
                </Table.Td>
                <Table.Td c={v.stock === 0 ? 'red' : v.stock < 5 ? 'orange' : undefined}>
                  {v.stock} ks
                </Table.Td>
                <Table.Td>{v.linesCount} ks</Table.Td>
                <Table.Td>{v.linesSum} €</Table.Td>
                <Table.Td>{v.reservationsCount}</Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Stack>
  );
};

const LiveSummary = ({ liveId }: { liveId: string }) => {
  const { data, loading } = useQuery(ordersTabletSummaryDocument, {
    variables: { where: { liveId: { equals: liveId } } },
    pollInterval: 15000,
    notifyOnNetworkStatusChange: true,
  });

  const { data: mottoData } = useQuery(liveMottoDocument, {
    pollInterval: 15000,
    notifyOnNetworkStatusChange: true,
  });

  return (
    <Group justify="space-between">
      <Text fz={48} fw={700}>
        {data?.ordersTabletSummary.total ?? 0} €
      </Text>
      {loading && <Loader size={16} />}
      {mottoData && (
        <Text fz={36} fw={500}>
          {mottoData.liveMotto}
        </Text>
      )}
      <Text fz={48} fw={700}>
        {data?.ordersTabletSummary.count ?? 0} objednávok
      </Text>
    </Group>
  );
};

export const TabletPage = () => {
  const { liveId } = useParams();

  const { data, refetch, loading } = useQuery(productsTabletDocument, {
    variables: {
      where: { active: { equals: true }, liveId: { equals: liveId } },
      orderBy: { updatedAt: SortOrder.Desc },
      take: 10,
    },
    pollInterval: 3000,
    notifyOnNetworkStatusChange: true,
  });

  if (!liveId || !data) return null;

  return (
    <Container my="md" fluid>
      <Affix position={{ bottom: 20, right: 20 }}>
        <ActionIcon
          size="xl"
          onClick={async () => {
            await refetch();
          }}
          loading={loading}
        >
          <IconRefresh />
        </ActionIcon>
      </Affix>
      <LiveSummary liveId={liveId} />
      <Stack>
        {data.products.map((product, i) => (
          <TabletVariant key={product.id} product={product} i={i} />
        ))}
      </Stack>
    </Container>
  );
};
